/**
 * The client for `vibe-server`, shared by every autoauction.com surface.
 *
 * Its endpoint methods sit over one private `sendRequest`, configured once
 * by the host and imported wherever data is read. The singleton shape is
 * deliberate and matches the event-runtime SDK configured beside it — one shape
 * for both clients beats a better shape for one.
 *
 * **The wire keeps its own words.** `/items` in a path and `vehicleId` on a
 * message are the boundary and are spelled the way the server spells them; the
 * methods on this side take the glossary's word, which is `asset`. The mapping
 * happens here and nowhere else — which is the rule this file exists to make
 * enforceable, and the reason it moved out of the mobile portal: a store that
 * knows the wire's shape cannot be shared with a second surface.
 *
 * **It runs anywhere.** Four methods used to reach for `window.location.origin`
 * as a base for the `URL` constructor, which made the module browser-only for a
 * reason nobody had chosen and made it untestable under this package's `node`
 * project. Every URL is built by one `buildUrl` doing string work, so there is
 * no `window` here and one shape reaches `sendRequest` instead of two.
 */
import { sameOriginDevProofUrl, sameOriginDevUrl } from '@hds/identity-client-sdk';
import { useLogger } from '../adapters/ports.js';
import { logRequestOutcome, normalizeRequestPath, requestTimestamp, VIBE_SERVER_API } from './requestLogging.js';

/**
 * Every list endpoint answers with an array, whatever it put on the wire.
 *
 * The wire is not consistent and there is no prospect of it becoming so
 * quickly: `/sales` and `/items/subsetByQuery` answer `{ matchedCount,
 * documents }`, while `/liked-lists` and `/devices/me` answer a bare
 * array. Two stores each carried a private theory about which — one wrote
 * `Array.isArray(response) ? response : (response?.documents ?? [])` and the
 * other reached straight for `result?.documents` and got `undefined` from the
 * endpoints that do not send one.
 *
 * A guess per call site is how a client ends up with as many opinions about the
 * server as it has callers. There is one here instead, and a caller gets an
 * array or an empty one.
 */
function documentsOf(response) {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.documents)) return response.documents;
    return [];
}

/**
 * The path a URL names, with the query and fragment dropped and an absolute
 * URL reduced to its path.
 *
 * Deliberately not the `URL` constructor: `baseAutoAuctionUri` may be relative
 * (the dev proxy's `/api`), which the constructor rejects without a base, and
 * the only base available in a browser is `window.location.origin` — which is
 * how this module came to be browser-only in the first place. String work has
 * neither problem and runs anywhere.
 */
function requestPath(url) {
    const withoutQuery = String(url).split('?')[0].split('#')[0];
    const schemeEnd = withoutQuery.indexOf('://');
    if (schemeEnd === -1) return withoutQuery;
    const afterAuthority = withoutQuery.slice(schemeEnd + 3);
    const firstSlash = afterAuthority.indexOf('/');
    return firstSlash === -1 ? '/' : afterAuthority.slice(firstSlash);
}

/**
 * The methods this client sends without content.
 *
 * RFC 9110 is permissive about `DELETE` and firm about `GET` — a client SHOULD
 * NOT generate content in either where the content has no defined semantics —
 * and content on a `DELETE` is the one that actually bites: it is
 * underspecified enough that proxies strip it and some servers reject the
 * request outright. Both of this client's `DELETE` callers,
 * `removeAssetFromLikedList` and `unregisterDevice`, address the record
 * entirely in the path, so there is nothing to send.
 *
 * `POST` is deliberately not here. Three methods call `sendRequest` with no
 * body and currently send `{}` — `logout`, `markConversationRead` and
 * `archiveLikedList`, which POSTs to `/archive` — and a server may well want
 * a JSON body on a POST.
 * Silencing those would be a behavior change on three live endpoints to fix a
 * problem on two others.
 */
const METHODS_SENT_WITHOUT_CONTENT = new Set(['GET', 'DELETE']);

/**
 * A failed request, named.
 *
 * The thrown error used to be a bare `Error` with two fields stapled on: it
 * named no request, and a caller could only recognize one by checking for a
 * `statusCode` property, because there was no class to ask about. Both are
 * fixed here — `instanceof ApiError` works, and `method`, `url` and `path` say
 * which call produced it.
 *
 * `statusCode` and `body` keep their exact previous shape, because callers read
 * them: session.store.js branches on `statusCode` 401 vs 404 to tell "not
 * logged in" from an outage, and LoginPage/SignupPage/ProfilePage read
 * `body.error` for `INVALID_CREDENTIALS`, `PENDING_APPROVAL` and
 * `EMAIL_IN_USE`. `body` stays **absent** rather than `undefined` when the
 * response was not JSON, which is what it did before.
 *
 * `path` is the field the logging seam wants: a URL carries the query string
 * and a per-record id, and logging it raw yields one "endpoint" per asset.
 * Collapsing ids to `:id` is the logger's business, but it should not have to
 * re-parse a URL to do it.
 */
class ApiError extends Error {
    constructor({ method, url, statusCode, body }) {
        const path = requestPath(url);
        super(`Received response status code: ${statusCode} (${method} ${path})`);
        this.name = 'ApiError';
        this.statusCode = statusCode;
        this.method = method;
        this.url = String(url);
        this.path = path;
        // Assigned only when there was one, so `Object.keys(err)` reads
        // ["statusCode", ...] and not a phantom `body: undefined`.
        if (body !== undefined) this.body = body;
    }
}

function createApiSdk() {
    // Private state.
    //
    // `customHeaderFn` returns headers to merge into every request — the app
    // sets it to supply Authorization. It was written as `async function () {}`,
    // which types as `() => Promise<void>`; that went unnoticed while this file
    // lived in the component library behind the app's `declare module` shim,
    // which typed the whole SDK as `any`.
    /** @type {() => Promise<Record<string, string>>} */
    let customHeaderFn = async function () { return {}; };
    let onUnauthorized = null;
    let baseAutoAuctionUri;

    /*****************************************************/
    /* Private Methods */
    /*****************************************************/
    /**
     * Every URL this client sends, built one way.
     *
     * There were two ways. Twenty-seven methods built a bare template string;
     * the four that needed a query string reached for `new URL(..., window.
     * location.origin)` instead — so `sendRequest` received either a string or
     * a `URL` and branched on which, and the module was browser-only for a
     * reason nobody had chosen. One shape reaching the seam removes that branch
     * here, in the 401 login check, and in anything layered underneath later.
     *
     * @param {string} path - Appended to `baseAutoAuctionUri`, leading slash included.
     * @param {Object} [query] - Entries with an `undefined` or `null` value are
     *   omitted, so a caller can pass optional parameters without composing the
     *   object first. An empty result appends no `?`, which is what
     *   `url.search = ''` did before.
     * @returns {string}
     */
    function buildUrl(path, query) {
        const url = `${baseAutoAuctionUri}${path}`;
        if (!query) return url;
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(query)) {
            if (value !== undefined && value !== null) params.append(key, value);
        }
        const search = params.toString();
        return search ? `${url}?${search}` : url;
    }

    /**
     * The one seam every request passes through, and the only place this client
     * signals anything. There are two signals and both have a case they must
     * NOT fire in, which is the half of a contract nothing counts:
     *
     * | Signal | Fires when | Does not fire when |
     * | --- | --- | --- |
     * | the thrown `ApiError` | any non-2xx | a 2xx, whatever the body |
     * | `onUnauthorized(err)` | a 401 | never — the sign-in path lives at the hosted login, so every 401 here is an expired or invalid session |
     *
     * A third case is bounded and stays bounded: a handler that throws does not
     * swallow the error. The call is wrapped and the error is rethrown either
     * way, so a broken `onUnauthorized` cannot turn a 401 into a resolved
     * promise.
     *
     * Two more behaviors here are load-bearing and easy to lose in a rewrite.
     * `GET` and `DELETE` carry no body, because some servers reject one — see
     * `METHODS_SENT_WITHOUT_CONTENT`, and note `POST` is not in it. And `statusCode`
     * on the thrown error is what session hydration branches on to tell "not
     * logged in" from an outage.
     *
     * Being the one seam is also what makes it the one place the app can
     * measure itself against the server — see
     * [requestLogging.js](./requestLogging.js) for why that seam is the whole
     * API surface.
     */
    async function sendRequest(url, method, body = {}) {
        const startedAt = requestTimestamp();
        // The credential may be a proof made for this one request, and it names
        // `sameOriginDevProofUrl(url)` — on a phone the configured host is the
        // laptop's LAN IP, which vibe-server does not list as a public origin.
        const customHeaders = await customHeaderFn({ method, url: sameOriginDevProofUrl(url) });
        // Body-less methods (GET/DELETE) must not advertise application/json:
        // Fastify rejects an empty JSON body with FST_ERR_CTP_EMPTY_JSON_BODY
        // (400), which is exactly what Home unfavorite hit on
        // DELETE /liked-lists/:id/assets/:assetId.
        const headers = { ...customHeaders };
        if (METHODS_SENT_WITHOUT_CONTENT.has(method)) {
            delete headers['Content-Type'];
            delete headers['content-type'];
        } else {
            // Pinned last, not first. Custom headers used to spread over the
            // defaults, so a host whose `customHeaderFn` returned a
            // `Content-Type` broke every JSON request in the app from the
            // one place nobody would look. Only this header is pinned:
            // `Authorization` is what the hook exists to supply and stays
            // settable, which is the extreme the fix must not swing to.
            headers['Content-Type'] = 'application/json';
        }
        let response;
        try {
            // Addressed at `url`, sent to `sameOriginDevUrl(url)`: on an https
            // phone page the configured http://localhost:4500 base is a URL
            // the browser will not fetch, so the request goes through the dev
            // server's own origin instead. The credential above names a third
            // URL, which is the origin the server's DPoP check lists.
            response = await fetch(sameOriginDevUrl(url), {
                method,
                mode: "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers,
                redirect: "follow",
                referrerPolicy: "no-referrer",
                body: METHODS_SENT_WITHOUT_CONTENT.has(method) ? undefined : JSON.stringify(body)
            });
        } catch (err) {
            // A rejected fetch is the offline/DNS/CORS case: no status, no
            // response, and the branches below never run. It is also the single
            // most informative failure a dealer on a bad connection produces,
            // so it must not be the one that goes unreported.
            logRequestOutcome({ api: VIBE_SERVER_API, method, url, startedAt, ok: false, err });
            throw err;
        }
        logRequestOutcome({
            api: VIBE_SERVER_API,
            method,
            url,
            startedAt,
            status: response.status,
            ok: response.ok
        });
        if (!response.ok) {
            let responseBody;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.indexOf('application/json') !== -1) {
                responseBody = await response.json();
            }
            const err = new ApiError({ method, url, statusCode: response.status, body: responseBody });
            // Surface 401s to whatever the app installed as the unauthorized
            // handler (typically: clear the token + redirect to /login).
            if (response.status === 401) {
                // Reported whether or not a handler is installed: a session
                // dying under the app is worth knowing about on its own, and
                // it is indistinguishable from a working app in the http
                // events alone.
                useLogger().warn(
                    { event: 'auth.unauthorized', method, path: normalizeRequestPath(url) },
                    'request rejected as unauthorized'
                );
                if (typeof onUnauthorized === 'function') {
                    try {
                        onUnauthorized(err);
                    } catch (handlerError) {
                        // Was a bare swallow. The handler clears the token and
                        // bounces to /login, so it throwing strands the user in
                        // a loop of failing calls — the exact failure nobody
                        // could see.
                        useLogger().error(
                            { event: 'auth.unauthorized_handler_failed', err: handlerError },
                            'unauthorized handler threw'
                        );
                    }
                }
            }
            throw err;
        } else {
            const contentType = response.headers.get('content-type');
            const contentLength = response.headers.get('content-length');
            let responseData;
            if (contentType && contentType.indexOf('application/json') !== -1) {
                responseData = await response.json();
            } else if (contentLength > 0) {
                responseData = await response.text();
            }
            return responseData;
        }
    }

    /*****************************************************/
    /* Public Methods */
    /*****************************************************/
    // `asset`, not `vehicle` — the glossary retires the generic, and the
    // liked-list methods below already say it. The path stays `/items`,
    // which is the server's word and therefore the boundary's.
    async function getAssetById(id) {
        const url = buildUrl(`/items/${id}`);
        return await sendRequest(url, 'GET')
    }

    /**
     * How the asset's latest run at the block ended, and when: `{ status,
     * closedAt, saleId, highBid }`, or null while it has not closed. vibe-server
     * records it from the event runtime's ITEM_BIDDING_ENDED webhook, because the
     * runtime serves clients no close time of its own.
     */
    async function getAssetResult(id) {
        const url = buildUrl(`/items/${encodeURIComponent(id)}/result`);
        const response = await sendRequest(url, 'GET');
        return response?.result ?? null;
    }

    /**
     * Requests the searchable properties & counts based on the query properties provided
     * Current searchable values: MAKE, MODEL, SERIES, BODYSTYLE
     * @param {String} searchable - The searchable criterion's options being requested
     * @param {Object} query
     * @param {String} query.YEAR - A range divided by a hyphen ( ex: 1971-1985 )
     * @param {Array} query.MAKE - An array of currently selected vehicles (ex: ['Ford'])
     * @param {Array} query.MODEL - The model of the vehicle (ex: ['F150'])
     * @param {Array} query.SERIES - The series of the vehicle (ex: ['Lariat'])
     * @param {Array} query.BODYSTYLE - The of the vehicle (ex: ['4dr 4-cyl 4-Spd AT (Natl)'])
     * @param {Array} query.AUCTIONID - The auction id where the vehicle will be for sale (ex: ['DAAW'])
     * @param {String} query.ODOMETERREADING - A range divided by a hyphen ( ex: 0-10000 )
     * @returns {Array} - An array of options with counts for the searchable criterion
     */
    async function getSearchableOptions(searchable, query = {}) {
        const url = buildUrl(`/items/getSearchableOptions/${searchable}`);
        return await sendRequest(url, 'POST', query);
    }

    /**
     * Requests a subset of assets based on startingIndex
     * @param {Object} query
     * @param {String} query.YEAR - A range divided by a hyphen ( ex: 1971-1985 )
     * @param {Array} query.MAKE - An array of currently selected vehicles (ex: ['Ford'])
     * @param {Array} query.MODEL - The model of the vehicle (ex: ['F150'])
     * @param {Array} query.SERIES - The series of the vehicle (ex: ['Lariat'])
     * @param {Array} query.BODYSTYLE - The of the vehicle (ex: ['4dr 4-cyl 4-Spd AT (Natl)'])
     * @param {Array} query.AUCTIONID - The auction id where the vehicle will be for sale (ex: ['DAAW'])
     * @param {String} query.ODOMETERREADING - A range divided by a hyphen ( ex: 0-10000 )
     * @param {String} query.fql - Optional FQL the server compiles and ANDs with the facet keys: the part of an AI search no facet key can hold. An FQL the server cannot run answers 400 UNSUPPORTED_FQL rather than widening the search.
     * @param {Object} subsetOptions
     * @param {Number} subsetOptions.startingIndex
     * @param {Number} subsetOptions.size
     * @returns {Object} - An object containing 3 properties: meta, documents, and searchables
     */
    async function getAssetsSubsetByQuery(queryProperties = {}, subsetOptions = {}) {
        const url = buildUrl('/items/subsetByQuery', {
            startingIndex: subsetOptions.startingIndex,
            size: subsetOptions.size
        });
        return await sendRequest(url, 'POST', queryProperties);
    }

    /**
     * Requests the cars that look like the ones in the caller's BidStack.
     *
     * The server's recommendations engine answers, scoring the whole catalog: see
     * documentation/auto-auction-com/recommendations-engine.md. The caller is the signed-in
     * dealer, so there is no id to pass.
     * @param {Object} [options]
     * @param {Number} [options.limit] - How many to ask for; the server bounds it.
     * @returns {Object} - `{ basedOn: { makes, models }, matchedCount, documents }`: what the
     *   BidStack says the dealer likes, how many cars matched, and the best of them as catalog
     *   cards, each with its `score`.
     */
    async function getRecommendations({ limit } = {}) {
        const url = buildUrl('/recommendations', { limit });
        return await sendRequest(url, 'GET');
    }

    /**
     * Requests a subset of assets matching an Extended-JSON Mongo filter.
     *
     * The assistant's door. Its `search_vehicles` tool answers with the filter
     * its FQL compiled to, and this is how the feed pages through those
     * matches — no translating a Mongo filter back into facet keys, which
     * cannot be done without losing what the dealer asked for.
     * @param {Object} filter - The Extended-JSON Mongo filter the assistant answered with
     * @param {Object} subsetOptions
     * @param {Number} subsetOptions.startingIndex
     * @param {Number} subsetOptions.size
     * @returns {Object} - An object containing 3 properties: meta, documents, and searchables
     */
    async function getAssetsByFilter(filter = {}, subsetOptions = {}) {
        const url = buildUrl('/items/searchByFilter', {
            startingIndex: subsetOptions.startingIndex,
            size: subsetOptions.size
        });
        return await sendRequest(url, 'POST', filter);
    }

    /**
     * Requests every scheduled sale (one document per lane).
     *
     * Explore uses this to badge catalog cards with "this runs Thursday" without
     * touching the event runtime — vibe-server is the sale authority, and the
     * feed must not take a hard dependency on the runtime being up.
     * @returns {Array} - Sale entities, one per lane. Unwrapped here so no caller has to know this endpoint sends an envelope.
     */
    async function getSales() {
        const url = buildUrl('/sales');
        return documentsOf(await sendRequest(url, 'GET'));
    }

    /**
     * Requests one sale's assets from the catalog, in run order, each carrying
     * the `runNumber` the block shows. The items keep the catalog's own field
     * names; `saleAssetOf` turns one into the shape a surface draws.
     * @returns {Array} - Catalog items, unwrapped from the envelope.
     */
    async function getSaleAssets(saleId) {
        const url = buildUrl(`/sales/${encodeURIComponent(saleId)}/assets`);
        return documentsOf(await sendRequest(url, 'GET'));
    }

    // Session/identity endpoints (/session/*) moved to
    // @hds/identity-client-sdk, which owns its own transport.
    // This client keeps only the auto-auction domain surface.

    /*****************************************************/
    /* Conversations + messages */
    /*****************************************************/
    /**
     * Threads the caller participates in, filtered on the CALLER's archive —
     * archive is per participant, so the two dealers in a thread can answer
     * differently. The two halves are disjoint: the default list is what is not
     * archived for the caller, `{ archived: true }` is what is.
     * @param {Object} [options]
     * @param {boolean} [options.archived] - Ask for the archived half instead of the open one.
     * @returns {Array} - Conversation entities. Unwrapped here, as every list method is.
     */
    async function getConversations({ archived } = {}) {
        const url = buildUrl('/conversations', archived ? { archived: true } : undefined);
        return documentsOf(await sendRequest(url, 'GET'));
    }

    async function getConversation(id) {
        const url = buildUrl(`/conversations/${id}`);
        return await sendRequest(url, 'GET');
    }

    async function getMessages(conversationId, { since, limit } = {}) {
        const url = buildUrl(`/conversations/${conversationId}/messages`, { since, limit });
        return documentsOf(await sendRequest(url, 'GET'));
    }

    async function sendMessage(conversationId, { kind, body = null, vehicleId = null, imageUrl = null, attachments = null, offer = null, schedule = null }) {
        const url = buildUrl(`/conversations/${conversationId}/messages`);
        const payload = { kind, body, vehicleId, imageUrl };
        if (Array.isArray(attachments) && attachments.length > 0) {
            payload.attachments = attachments;
        }
        if (offer) payload.offer = offer;
        if (schedule) payload.schedule = schedule;
        return await sendRequest(url, 'POST', payload);
    }

    async function markConversationRead(conversationId) {
        const url = buildUrl(`/conversations/${conversationId}/read`);
        return await sendRequest(url, 'POST');
    }

    /**
     * Humans the caller may start a conversation with. Scoping is entirely
     * server-side — callers that aren't allowed to start threads get an
     * empty list rather than an error.
     * @returns {Array} - [{ id, name, company }]. Unwrapped here, as every list method is.
     */
    async function getConversationRecipients() {
        const url = buildUrl('/conversations/recipients');
        return documentsOf(await sendRequest(url, 'GET'));
    }

    // No `leadId`. ConversationsRoutes.mjs has not read one since the leads
    // concept was removed, so sending it was a parameter the server ignored. If
    // leads ever return, the server accepts the field again before the client
    // sends it — re-adding a parameter to two methods is smaller than keeping
    // one nothing reads.
    async function createConversation({ participantIds, dealerId = null, category = null, name = null }) {
        const url = buildUrl('/conversations');
        const payload = { participantIds };
        if (dealerId) payload.dealerId = dealerId;
        if (category) payload.category = category;
        if (name) payload.name = name;
        return await sendRequest(url, 'POST', payload);
    }

    async function updateConversation(id, patch = {}) {
        const url = buildUrl(`/conversations/${id}`);
        return await sendRequest(url, 'PATCH', patch);
    }

    /*****************************************************/
    /* Liked lists */
    /*****************************************************/
    //
    // The dealer's lists of liked assets. The wire says `liked-lists` too now: the
    // path, the Mongo collection and the built-in list's slug all moved with
    // renameToLikedLists.mjs. `/saved-categories` is unmounted, not aliased.
    async function getLikedLists() {
        const url = buildUrl('/liked-lists');
        return documentsOf(await sendRequest(url, 'GET'));
    }

    async function createLikedList(body) {
        const url = buildUrl('/liked-lists');
        return await sendRequest(url, 'POST', body);
    }

    async function updateLikedList(id, body) {
        const url = buildUrl(`/liked-lists/${id}`);
        return await sendRequest(url, 'PUT', body);
    }

    async function archiveLikedList(id) {
        const url = buildUrl(`/liked-lists/${id}/archive`);
        return await sendRequest(url, 'POST');
    }

    /**
     * Append one asset to a liked list via the dedicated
     * per-item endpoint. The server stamps `addedBy` from the JWT so
     * the dealer-vs-owner distinction is never client-trusted. Works
     * for owners AND dealers in `sharedWith`; the latter cannot use
     * the bulk PUT route since it's owner-only.
     * @param {String} categoryId
     * @param {String} assetId
     * @param {String|null} [note]
     * @param {{ targetPrice?: Number|null, items?: Array<{label:String,cost:Number}>|null }} [extra]
     *   Optional planning-worksheet fields persisted on the membership entry.
     * @returns {Promise<Object>} The updated category document.
     */
    async function addAssetToLikedList(categoryId, assetId, note = null, extra = {}) {
        const url = buildUrl(`/liked-lists/${categoryId}/assets`);
        const body = { assetId, note };
        if (extra.targetPrice !== undefined) body.targetPrice = extra.targetPrice;
        if (extra.items !== undefined) body.items = extra.items;
        return await sendRequest(url, 'POST', body);
    }

    /**
     * Remove one asset entry from a liked list via the dedicated
     * per-item endpoint. Owners can remove any entry; dealers in
     * `sharedWith` can only remove entries they themselves added.
     * @param {String} categoryId
     * @param {String} assetId
     * @returns {Promise<Object>} The updated category document.
     */
    async function removeAssetFromLikedList(categoryId, assetId) {
        const url = buildUrl(`/liked-lists/${categoryId}/assets/${assetId}`);
        return await sendRequest(url, 'DELETE');
    }

    /*****************************************************/
    /* Devices (push notifications) */
    /*****************************************************/
    /**
     * Register the current device's Capacitor push token with the server.
     * Upserts on `{ type: 'device', token }`, so calling this on every
     * launch is safe (re-registration updates ownerId + lastSeenAt).
     * @param {Object} body
     * @param {'ios'|'android'} body.platform
     * @param {String} body.token - Vendor-issued push token.
     * @param {String} [body.locale] - Reporting locale (e.g. navigator.language).
     */
    async function registerDevice({ platform, token, locale } = {}) {
        const url = buildUrl('/devices');
        const payload = { platform, token };
        if (locale) payload.locale = locale;
        return await sendRequest(url, 'POST', payload);
    }

    /**
     * Drop a device registration. Used on logout / token rotation.
     * @param {String} token - The vendor-issued push token to unregister.
     */
    async function unregisterDevice(token) {
        const url = buildUrl(`/devices/${encodeURIComponent(token)}`);
        return await sendRequest(url, 'DELETE');
    }

    /** List devices currently registered to the authenticated user. */
    async function listMyDevices() {
        const url = buildUrl('/devices/me');
        return documentsOf(await sendRequest(url, 'GET'));
    }

    /*****************************************************/
    /* Offers */
    /*****************************************************/
    // An offer is scoped to its two parties, and WHICH party the caller is
    // decides which of these the server will let them make. The client sends
    // the same call either way; the server answers 403 or 409 when the move is
    // not theirs to make.
    async function getOffers({ state, group, assetId } = {}) {
        const url = buildUrl('/offers', { state, group, assetId });
        return documentsOf(await sendRequest(url, 'GET'));
    }

    async function getOffer(id) {
        const url = buildUrl(`/offers/${id}`);
        return await sendRequest(url, 'GET');
    }

    /**
     * `asset` is the display snapshot the offer keeps, so My Offers can name
     * what an offer is on without a second read of the catalog.
     */
    async function createOffer({ assetId, amount, note = null, sellerId = null, sellerName = null, ask = null, mmr = null, highBid = null, asset = null }) {
        const url = buildUrl('/offers');
        return await sendRequest(url, 'POST', { assetId, amount, note, sellerId, sellerName, ask, mmr, highBid, asset });
    }

    async function reviseOffer(id, { moveId = null, amount, note = null }) {
        const url = buildUrl(`/offers/${id}/revise`);
        return await sendRequest(url, 'POST', { moveId, amount, note });
    }

    async function counterOffer(id, { amount, note = null }) {
        const url = buildUrl(`/offers/${id}/counter`);
        return await sendRequest(url, 'POST', { amount, note });
    }

    async function acceptOffer(id, { note = null } = {}) {
        const url = buildUrl(`/offers/${id}/accept`);
        return await sendRequest(url, 'POST', { note });
    }

    async function declineOffer(id) {
        const url = buildUrl(`/offers/${id}/decline`);
        return await sendRequest(url, 'POST');
    }

    async function withdrawOffer(id) {
        const url = buildUrl(`/offers/${id}/withdraw`);
        return await sendRequest(url, 'POST');
    }

    async function openOffer(id) {
        const url = buildUrl(`/offers/${id}/open`);
        return await sendRequest(url, 'POST');
    }

    /** Link the thread the offer is negotiated in, and answer the offer carrying its id. */
    async function ensureOfferConversation(id) {
        const url = buildUrl(`/offers/${id}/ensure-conversation`);
        return await sendRequest(url, 'POST');
    }

    /*****************************************************/
    /* Purchases */
    /*****************************************************/
    async function getPurchases({ outcome, stage } = {}) {
        const url = buildUrl('/purchases', { outcome, stage });
        return documentsOf(await sendRequest(url, 'GET'));
    }

    async function getPurchase(id) {
        const url = buildUrl(`/purchases/${id}`);
        return await sendRequest(url, 'GET');
    }

    /** Records that the buyer paid and how. Nothing is charged: there is no payment provider behind it. */
    async function payPurchase(id, payType) {
        const url = buildUrl(`/purchases/${id}/pay`);
        return await sendRequest(url, 'POST', { payType });
    }

    async function advancePurchase(id, stage = null) {
        const url = buildUrl(`/purchases/${id}/advance`);
        return await sendRequest(url, 'POST', { stage });
    }

    /*****************************************************/
    /* Sale desk notices */
    /*****************************************************/
    // A sale is named by its catalog id, which in this product is the sale's
    // name, so it is encoded into the path rather than assumed url-safe.
    //
    // The read answers the sale's notices AND the caller's own mute and read
    // state in one trip, because a strip cannot draw one without the others.
    async function getSaleDeskNotices(saleId) {
        const url = buildUrl(`/sales/${encodeURIComponent(saleId)}/desk-notices`);
        const response = await sendRequest(url, 'GET');
        return {
            documents: documentsOf(response),
            muted: response?.muted === true,
            readNoticeIds: response?.readNoticeIds ?? []
        };
    }

    async function markSaleDeskNoticesRead(saleId, ids = null) {
        const url = buildUrl(`/sales/${encodeURIComponent(saleId)}/desk-notices/read`);
        return await sendRequest(url, 'POST', { ids });
    }

    async function setSaleDeskMuted(saleId, muted) {
        const url = buildUrl(`/sales/${encodeURIComponent(saleId)}/desk-mute`);
        return await sendRequest(url, 'PUT', { muted });
    }

    /*****************************************************/
    /* App notices */
    /*****************************************************/
    async function getAppNotices() {
        const url = buildUrl('/app-notices');
        return documentsOf(await sendRequest(url, 'GET'));
    }

    async function dismissAppNotice(id) {
        const url = buildUrl(`/app-notices/${id}/dismiss`);
        return await sendRequest(url, 'POST');
    }

    /*****************************************************/
    /* Weekly budget */
    /*****************************************************/
    async function getBudget() {
        const url = buildUrl('/me/budget');
        return await sendRequest(url, 'GET');
    }

    async function setBudget({ limit, warnNearLimit = true }) {
        const url = buildUrl('/me/budget');
        return await sendRequest(url, 'PUT', { limit, warnNearLimit });
    }

    /*****************************************************/
    /* Saved searches */
    /*****************************************************/
    async function getSavedSearches() {
        const url = buildUrl('/saved-searches');
        return documentsOf(await sendRequest(url, 'GET'));
    }

    async function createSavedSearch({ name, query = {}, summary = null }) {
        const url = buildUrl('/saved-searches');
        return await sendRequest(url, 'POST', { name, query, summary });
    }

    async function deleteSavedSearch(id) {
        const url = buildUrl(`/saved-searches/${id}`);
        return await sendRequest(url, 'DELETE');
    }

    /*****************************************************/
    /* Event runtime — live auction bidding */
    /*****************************************************/
    // Place a live bid on a lot as the given dealer badge.
    async function proposeBid(itemId, { amount, badge }) {
        const url = buildUrl(`/bidding/item-raw-bidding/${encodeURIComponent(itemId)}/append`);
        return await sendRequest(url, 'PUT', { value: { rawEntryType: 'proposeBid', amount, badge } });
    }
    // Arm / update a proxy (max) bid on a lot.
    async function upsertProxyBid(itemId, { amount, badge }) {
        const url = buildUrl(`/bidding/item-raw-bidding/${encodeURIComponent(itemId)}/append`);
        return await sendRequest(url, 'PUT', { value: { rawEntryType: 'upsertProxyBid', amount, badge } });
    }
    // Clear a proxy (max) bid on a lot.
    async function deleteProxyBid(itemId, { badge }) {
        const url = buildUrl(`/bidding/item-raw-bidding/${encodeURIComponent(itemId)}/append`);
        return await sendRequest(url, 'PUT', { value: { rawEntryType: 'deleteProxyBid', badge } });
    }

    /*****************************************************/
    /* Return Public API */
    /*****************************************************/
    return {
        proposeBid,
        upsertProxyBid,
        deleteProxyBid,
        sendRequest,
        getAssetById,
        getAssetResult,
        getSearchableOptions,
        getAssetsSubsetByQuery,
        getRecommendations,
        getAssetsByFilter,
        getSales,
        getSaleAssets,
        getConversations,
        getConversation,
        getMessages,
        sendMessage,
        markConversationRead,
        getConversationRecipients,
        createConversation,
        updateConversation,
        getLikedLists,
        createLikedList,
        updateLikedList,
        archiveLikedList,
        addAssetToLikedList,
        removeAssetFromLikedList,
        registerDevice,
        unregisterDevice,
        listMyDevices,
        getOffers,
        getOffer,
        createOffer,
        reviseOffer,
        counterOffer,
        acceptOffer,
        declineOffer,
        withdrawOffer,
        openOffer,
        ensureOfferConversation,
        getPurchases,
        getPurchase,
        payPurchase,
        advancePurchase,
        getSaleDeskNotices,
        markSaleDeskNoticesRead,
        setSaleDeskMuted,
        getAppNotices,
        dismissAppNotice,
        getBudget,
        setBudget,
        getSavedSearches,
        createSavedSearch,
        deleteSavedSearch,

        // ---- Configuration -------------------------------------------
        //
        // Properties rather than a `configure()` call, and that is a decision
        // rather than an accident: the event-runtime SDK is configured three
        // lines further down main.js with the same two property names, and one
        // shape for both clients beats a better shape for one. From inside this
        // file alone the shape looks arbitrary, which is why it is written here.
        //
        // All three are set once at boot. None validates, and none can be
        // cleared.

        /**
         * Headers merged into every request — the app sets this to supply
         * `Authorization`. It is a hook rather than a port: the function it is
         * given happens to read tokenStorage, and this client neither knows
         * that nor checks it. The virtue worth preserving is that the client
         * has no storage dependency of its own to break on a native build.
         * @type {() => Promise<Record<string, string>>}
         */
        get customHeaderFn() {
            return customHeaderFn;
        },
        set customHeaderFn(newCustomHeaderFn) {
            // Checked here because the alternative is a `TypeError:
            // customHeaderFn is not a function` at the next request, in a stack
            // naming this client rather than the line that misconfigured it.
            if (typeof newCustomHeaderFn !== 'function') {
                throw new TypeError('customHeaderFn must be a function returning headers');
            }
            customHeaderFn = newCustomHeaderFn;
        },
        /**
         * Called with the error when a request 401s.
         * The app installs one that clears the token and bounces to `/login`,
         * behind a latch in main.js that stops a burst of parallel 401s firing
         * several redirects — the latch lives there, not here, and any rework
         * of this contract has to keep it working.
         * @type {((error: Error) => void) | null}
         */
        get onUnauthorized() {
            return onUnauthorized;
        },
        set onUnauthorized(newHandler) {
            // `null` is meaningful — it is how the app clears the handler — so
            // it is the one non-function this accepts.
            if (newHandler !== null && typeof newHandler !== 'function') {
                throw new TypeError('onUnauthorized must be a function or null');
            }
            onUnauthorized = newHandler;
        },
        /**
         * The origin every URL is built against, via `buildUrl`. May be
         * relative — the dev proxy's `/api` — which is exactly why `buildUrl`
         * does string work rather than calling the `URL` constructor, whose
         * need for a base is what made four of these methods browser-only.
         * @type {string}
         */
        get baseAutoAuctionUri() {
            return baseAutoAuctionUri;
        },
        set baseAutoAuctionUri(newBaseAutoAuctionUri) {
            if (typeof newBaseAutoAuctionUri !== 'string' || newBaseAutoAuctionUri === '') {
                throw new TypeError('baseAutoAuctionUri must be a non-empty string');
            }
            baseAutoAuctionUri = newBaseAutoAuctionUri;
        }
    };
}

// SINGLETON instance pattern (ESM modules in the browser). The app imports the
// default and configures it once in main.js.
const autoAuctionApiSdk = createApiSdk();

/**
 * A second, independently configured client.
 *
 * Exported beside the singleton rather than instead of it: the singleton is the
 * app's shape and stays. What the factory buys is a client whose configuration
 * nothing else has touched — which is the first thing a test needs and the one
 * thing a module-scoped singleton cannot give it without a module reset between
 * every case.
 */
export { ApiError, autoAuctionApiSdk, createApiSdk };
export default autoAuctionApiSdk;
